import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - añadir token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = sessionStorage.getItem('accessToken');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - refresh token automático
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true });
        sessionStorage.setItem('accessToken', data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        sessionStorage.removeItem('accessToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  profile: () => api.get('/auth/profile'),
  updateProfile: (data: Record<string, unknown>) => api.put('/auth/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', data),
  refresh: () => api.post('/auth/refresh'),
};

// Projects
export const projectsAPI = {
  list: (params?: Record<string, unknown>) => api.get('/projects', { params }),
  create: (data: Record<string, unknown>) => api.post('/projects', data),
  get: (id: string) => api.get(`/projects/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  members: (id: string) => api.get(`/projects/${id}/members`),
  addMember: (id: string, memberId: string) => api.post(`/projects/${id}/team`, { memberId }),
  removeMember: (id: string, memberId: string) => api.delete(`/projects/${id}/team/${memberId}`),
  // Riesgos
  risks: (id: string) => api.get(`/projects/${id}/risks`),
  createRisk: (id: string, data: Record<string, unknown>) => api.post(`/projects/${id}/risks`, data),
  updateRisk: (id: string, riskId: string, data: Record<string, unknown>) => api.put(`/projects/${id}/risks/${riskId}`, data),
  deleteRisk: (id: string, riskId: string) => api.delete(`/projects/${id}/risks/${riskId}`),
  // Control de cambios
  changes: (id: string) => api.get(`/projects/${id}/changes`),
  createChange: (id: string, data: Record<string, unknown>) => api.post(`/projects/${id}/changes`, data),
  approveChange: (id: string, changeId: string, approval: string) => api.patch(`/projects/${id}/changes/${changeId}/approval`, { approval }),
  deleteChange: (id: string, changeId: string) => api.delete(`/projects/${id}/changes/${changeId}`),
  // Stakeholders
  stakeholders: (id: string) => api.get(`/projects/${id}/stakeholders`),
  createStakeholder: (id: string, data: Record<string, unknown>) => api.post(`/projects/${id}/stakeholders`, data),
  updateStakeholder: (id: string, shId: string, data: Record<string, unknown>) => api.put(`/projects/${id}/stakeholders/${shId}`, data),
  deleteStakeholder: (id: string, shId: string) => api.delete(`/projects/${id}/stakeholders/${shId}`),
  // Lecciones aprendidas
  lessons: (id: string) => api.get(`/projects/${id}/lessons`),
  createLesson: (id: string, data: Record<string, unknown>) => api.post(`/projects/${id}/lessons`, data),
  deleteLesson: (id: string, lessonId: string) => api.delete(`/projects/${id}/lessons/${lessonId}`),
};

// Tasks
export const tasksAPI = {
  list: (params?: Record<string, unknown>) => api.get('/tasks', { params }),
  kanban: (projectId: string) => api.get('/tasks/kanban', { params: { projectId } }),
  calendar: (params?: Record<string, unknown>) => api.get('/tasks/calendar', { params }),
  myWork: () => api.get('/tasks/my-work'),
  create: (data: Record<string, unknown>) => api.post('/tasks', data),
  get: (id: string) => api.get(`/tasks/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/tasks/${id}`, data),
  updateStatus: (id: string, status: string) => api.patch(`/tasks/${id}/status`, { status }),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  // Subtareas
  subtasks: (id: string) => api.get(`/tasks/${id}/subtasks`),
  createSubtask: (id: string, title: string) => api.post(`/tasks/${id}/subtasks`, { title }),
  updateSubtask: (id: string, subId: string, data: { title?: string; done?: boolean }) =>
    api.patch(`/tasks/${id}/subtasks/${subId}`, data),
  deleteSubtask: (id: string, subId: string) => api.delete(`/tasks/${id}/subtasks/${subId}`),
  // Comentarios
  comments: (id: string) => api.get(`/tasks/${id}/comments`),
  createComment: (id: string, data: { content: string; mentions?: string[] }) => api.post(`/tasks/${id}/comments`, data),
  deleteComment: (id: string, commentId: string) => api.delete(`/tasks/${id}/comments/${commentId}`),
  // Registro de atrasos
  createDelayLog: (id: string, data: { reason: string; oldDueDate?: string; newDueDate?: string }) =>
    api.post(`/tasks/${id}/delay-log`, data),
  getDelayLogs: (id: string) => api.get(`/tasks/${id}/delay-log`),
};

// Sprints
export const sprintsAPI = {
  list: (projectId: string) => api.get('/sprints', { params: { projectId } }),
  create: (data: Record<string, unknown>) => api.post('/sprints', data),
  get: (id: string) => api.get(`/sprints/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/sprints/${id}`, data),
  start: (id: string) => api.post(`/sprints/${id}/start`),
  complete: (id: string) => api.post(`/sprints/${id}/complete`),
  recoverTasks: (id: string) => api.post(`/sprints/${id}/recover-tasks`),
  burndown: (id: string) => api.get(`/sprints/${id}/burndown`),
  velocity: (id: string) => api.get(`/sprints/${id}/velocity`),
  standup: (id: string) => api.get(`/sprints/${id}/standup`),
  createStandup: (id: string, data: { yesterday: string; today: string; blockers?: string }) =>
    api.post(`/sprints/${id}/standup`, data),
  retrospective: (id: string) => api.get(`/sprints/${id}/retrospective`),
  setWip: (id: string, wipLimit: number | null) => api.patch(`/sprints/${id}/wip`, { wipLimit }),
  delete: (id: string) => api.delete(`/sprints/${id}`),
  assignTasks: (id: string, taskIds: string[]) => api.patch(`/sprints/${id}/tasks`, { taskIds }),
  addTeamMember: (id: string, memberId: string) => api.post(`/sprints/${id}/team/${memberId}`),
  removeTeamMember: (id: string, memberId: string) => api.delete(`/sprints/${id}/team/${memberId}`),
  capacity: (id: string) => api.get(`/sprints/${id}/capacity`),
};

// CRM
export const crmAPI = {
  stats: () => api.get('/crm/stats'),
  contacts: (params?: Record<string, unknown>) => api.get('/crm/contacts', { params }),
  createContact: (data: Record<string, unknown>) => api.post('/crm/contacts', data),
  updateContact: (id: string, data: Record<string, unknown>) => api.put(`/crm/contacts/${id}`, data),
  deleteContact: (id: string) => api.delete(`/crm/contacts/${id}`),
  opportunities: (params?: Record<string, unknown>) => api.get('/crm/opportunities', { params }),
  createOpportunity: (data: Record<string, unknown>) => api.post('/crm/opportunities', data),
  updateOpportunity: (id: string, data: Record<string, unknown>) => api.put(`/crm/opportunities/${id}`, data),
  deleteOpportunity: (id: string) => api.delete(`/crm/opportunities/${id}`),
};

// Reports
export const reportsAPI = {
  summary: () => api.get('/reports/summary'),
  dashboard: () => api.get('/reports/dashboard'),
  project: (id: string, params?: { month?: number; year?: number }) => api.get(`/reports/project/${id}`, { params }),
  projectList: (id: string) => api.get(`/reports/project/${id}/list`),
  saveReport: (projectId: string, data: { title: string; type?: string; content?: string }) =>
    api.post(`/reports/project/${projectId}/save`, data),
forecast: (projectId: string) => api.get(`/reports/forecast/${projectId}`),
  delayLogs: (projectId: string, params?: { month?: number; year?: number }) => api.get(`/reports/project/${projectId}/delay-logs`, { params }),
  // legacy
  list: () => api.get('/reports/summary'),
  kpis: () => api.get('/reports/summary'),
};

// AI
export const aiAPI = {
  analyze: (type: string, context: Record<string, unknown>) =>
    api.post('/ai/analyze', { type, context, language: 'es' }),
  status: () => api.get('/ai/status'),
  test: () => api.get('/ai/test'),
  testGemini: () => api.get('/ai/test'),
  testGroq: () => api.get('/ai/test-groq'),
  testOpenAI: () => api.get('/ai/test-openai'),
  setProviderEnabled: (provider: 'gemini' | 'groq' | 'openai', enabled: boolean) =>
    api.post(`/ai/providers/${provider}`, { enabled }),
  updateKeys: (keys: { geminiKey?: string; groqKey?: string; openaiKey?: string }) =>
    api.post('/ai/keys', keys),
};

// Users management
export const usersAPI = {
  list: () => api.get('/users'),
  roles: () => api.get('/users/roles'),
  createRole: (data: { name: string; description?: string }) => api.post('/users/roles', data),
  updateRole: (id: string, data: { name?: string; description?: string }) => api.put(`/users/roles/${id}`, data),
  deleteRole: (id: string) => api.delete(`/users/roles/${id}`),
  create: (data: Record<string, unknown>) => api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};


// Time Entries
export const timeEntriesAPI = {
  list: (taskId: string) => api.get(`/tasks/${taskId}/time-entries`),
  create: (taskId: string, data: Record<string, unknown>) =>
    api.post(`/tasks/${taskId}/time-entries`, data),
  update: (entryId: string, data: Record<string, unknown>) =>
    api.patch(`/tasks/time-entries/${entryId}`, data),
  delete: (entryId: string) => api.delete(`/tasks/time-entries/${entryId}`),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id: string) => api.delete(`/notifications/${id}`),
  pushPublicKey: () => api.get('/notifications/push/public-key'),
  subscribePush: (subscription: Record<string, unknown>) =>
    api.post('/notifications/push/subscribe', { subscription }),
  unsubscribePush: (endpoint: string) =>
    api.delete('/notifications/push/unsubscribe', { data: { endpoint } }),
  pushTest: () => api.post('/notifications/push/test'),
};

// Gantt
export const ganttAPI = {
  getData: (projectId: string) => api.get(`/gantt/${projectId}`),
  updateTaskDates: (projectId: string, taskId: string, data: { startDate?: string; endDate?: string }) =>
    api.put(`/gantt/${projectId}/tasks/${taskId}`, data),
  // Milestones
  milestones: (projectId: string) => api.get(`/gantt/${projectId}/milestones`),
  createMilestone: (projectId: string, data: Record<string, unknown>) =>
    api.post(`/gantt/${projectId}/milestones`, data),
  updateMilestone: (projectId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/gantt/${projectId}/milestones/${id}`, data),
  deleteMilestone: (projectId: string, id: string) =>
    api.delete(`/gantt/${projectId}/milestones/${id}`),
};

// Client Portal
export const clientPortalAPI = {
  createInvite: (projectId: string, data: { email: string; name: string; expiresInDays?: number }) =>
    api.post(`/projects/${projectId}/invites`, data),
  listInvites: (projectId: string) => api.get(`/projects/${projectId}/invites`),
  revokeInvite: (inviteId: string) => api.delete(`/invites/${inviteId}`),
  getPortalData: (token: string) => api.get(`/portal/${token}`),
};

// Backup / Restore
export const backupAPI = {
  exportBackup: () =>
    api.get('/backup/export', { responseType: 'blob' }),
  importBackup: (backup: unknown) =>
    api.post('/backup/import', { backup }),
};

// Portfolio & Capacity
export const portfolioAPI = {
  getOverview: () => api.get('/portfolio'),
  getCapacity: (projectId?: string) => api.get('/portfolio/capacity', { params: { projectId } }),
  getBurndown: () => api.get('/portfolio/burndown'),
  getRisks: () => api.get('/portfolio/risks'),
};

// Approval Workflows
export const approvalsAPI = {
  listByProject: (projectId: string) => api.get(`/projects/${projectId}/approvals`),
  addApprover: (changeId: string, data: { approverEmail: string; approverName?: string }) =>
    api.post(`/changes/${changeId}/approvals`, data),
  decide: (approvalId: string, data: { decision: string; comment?: string }) =>
    api.patch(`/approvals/${approvalId}/decide`, data),
  getChange: (changeId: string) => api.get(`/changes/${changeId}/approvals`),
};

// Integrations Hub
export const integrationsAPI = {
  listByProject: (projectId: string) => api.get(`/projects/${projectId}/integrations`),
  create: (projectId: string, data: { type: string; name: string; config: object }) =>
    api.post(`/projects/${projectId}/integrations`, data),
  update: (integrationId: string, data: object) => api.patch(`/integrations/${integrationId}`, data),
  delete: (integrationId: string) => api.delete(`/integrations/${integrationId}`),
  getEvents: (integrationId: string) => api.get(`/integrations/${integrationId}/events`),
  test: (integrationId: string) => api.post(`/integrations/${integrationId}/test`),
};

// Templates
export const templatesAPI = {
  list: () => api.get('/templates'),
  apply: (projectId: string, data: { templateId: string; startDate: string }) =>
    api.post(`/projects/${projectId}/apply-template`, data),
};

// Email / SMTP
export const emailAPI = {
  status: () => api.get('/email/status'),
  configure: (data: {
    host: string; port: number; secure: boolean;
    user: string; password: string; fromName: string; fromEmail: string;
  }) => api.post('/email/config', data),
  test: (testEmail: string) => api.post('/email/test', { testEmail }),
  remove: () => api.delete('/email/config'),
};

// Bitácora de Proyecto
export const bitacoraAPI = {
  list: (projectId: string, params?: { type?: string; from?: string; to?: string }) =>
    api.get('/project-logs', { params: { projectId, ...params } }),
  create: (data: { projectId: string; type: string; title: string; description: string; date?: string; taskId?: string }) =>
    api.post('/project-logs', data),
  update: (id: string, data: { type?: string; title?: string; description?: string; date?: string; taskId?: string | null }) =>
    api.put(`/project-logs/${id}`, data),
  delete: (id: string) =>
    api.delete(`/project-logs/${id}`),
  addReply: (logId: string, content: string) =>
    api.post(`/project-logs/${logId}/replies`, { content }),
  deleteReply: (replyId: string) =>
    api.delete(`/project-logs/replies/${replyId}`),
};

// User restrictions (admin)
export const userRestrictionsAPI = {
  get: (userId: string) => api.get(`/users/${userId}/restrictions`),
  update: (userId: string, restrictions: string[]) =>
    api.put(`/users/${userId}/restrictions`, { restrictions }),
};
